/**
 * SidebarV4 — the shared PMS/portal left navigation rail (desktop ≥900px).
 *
 * SCRUM-298 / MENU-2 (epic SCRUM-296): the full, parameterized sidebar. Moved
 * down from `widgets/pms-shell-v4` to the shared entities tier (beside
 * `MobileNavDrawer`) and built kit-ready so the real 2nd consumer (the owner
 * shell) can adopt it without a rewrite — at which point it promotes to
 * `@sparta/ui-kit` per ADR `frontend-0004` (second-consumer trigger). It stays
 * admin-local until then (no speculative kit residency while the component is
 * still evolving across MENU-2/4/5).
 *
 * Because it now lives in `entities`, it may NOT import the PMS-specific
 * `NAV_V4` (entities must not depend on a `widgets` slice, ADR `frontend-0001`).
 * Therefore `navModel` is a REQUIRED prop (no PMS default) and the generic
 * `NavNode` contract lives in `./types.ts`; the host (`PmsShellV4`) passes
 * `NAV_V4`.
 *
 * Behavior (full delta over the MENU-1 skeleton):
 *  - **Collapse rail** (`collapsed`): width `var(--sidebar-w-collapsed)`,
 *    labels + chevrons + footer Edit button hidden, icons only.
 *  - **Collapsed group rows do NOT route** — a group id is never a route, so
 *    firing `onSelect(groupId)` would be a dead click. A collapsed group click
 *    instead calls `onToggleCollapse()` (expands the rail) and auto-opens that
 *    group, landing the user on the expanded rail with the group's children
 *    visible. Collapsed *routable leaves* call `onSelect(id)` normally.
 *  - **Agency = route + expand**: expanded → `onSelect('agency')` (wired to the
 *    `/agency` route in MENU-6 / SCRUM-302) AND toggles the agent quick-list;
 *    collapsed → behaves like a collapsed group (`onToggleCollapse()` + open the
 *    quick-list), still NEVER firing `onSelect('agency')` while collapsed — the
 *    collapsed affordance reveals the quick-list rather than routing.
 *  - **Group toggle = row OR independent chevron**: the chevron is a separate
 *    focusable control that `stopPropagation`s — it toggles only, never routes.
 *  - **Active-child parent accent + auto-open**: a closed parent of the active
 *    child reads as active; the owning group auto-opens on mount AND whenever
 *    `active` changes — the host stays mounted across nested-route changes, so
 *    navigating into a child must reveal it (Simon P3, SCRUM-298). The
 *    auto-open MERGES into the open set (never clobbers groups the user
 *    toggled by hand).
 *  - **Routability / disabled contract**: a routable-destination row (a
 *    top-level leaf or a group child) whose id is not routable — per the
 *    injected `isRoutable` predicate — renders DISABLED, the same contract
 *    `MobileNavDrawer` applies on compact, never a dead button that silently
 *    no-ops on click (Simon P2, SCRUM-298). Group rows + Agency are EXEMPT
 *    (they toggle/expand, they don't route).
 *  - **Agent quick-list** renders from the injected `agents` prop (default
 *    `[]`, empty-safe) — NOT `window.AGENTS` (decoupled from load order).
 *    Rows fire `onAgentChat(id)`. When `agents` is empty, the expanded Agency
 *    shows an honest "No agents connected yet" line (MENU-5 / SCRUM-301) — never
 *    fabricated rows; real agents + chat arrive when the backend lands.
 *  - **Edit-menu mode** (MENU-4 / SCRUM-300): the footer Edit button toggles an
 *    edit mode (EXPANDED rail only) that hides/restores top-level items,
 *    persisted to `localStorage[storageKey]` via the shared `useHiddenNav`
 *    store. In edit mode each row shows an add/remove control + a row click
 *    toggles hide/restore (groups don't expand, children don't show — hideable
 *    scope is top-level only); outside edit mode hidden items are filtered out.
 *    Dashboard is non-hideable (the nav never renders empty). The same hidden
 *    set drives the compact `MobileNavDrawer` — the host (`PmsShellV4`) reads
 *    `useHiddenNav` with the SAME `storageKey` and filters the drawer.
 *  - **Hidden scrollbar, keyboard-scrollable** via the `.sparta-v4-nav-scroll`
 *    CSS rule (index.css) — NOT an inline numeric `style` (SCRUM-78). The
 *    focus ring survives the hidden-scrollbar region.
 *  - **Reduced motion**: the chevron rotation + group expand honor
 *    `prefers-reduced-motion` (the transition class is dropped when reduced).
 *
 * Token/lint compliance: colors via `bg-[var(--token)]`; widths via the
 * `--sidebar-w*` vars (no pixel-literal arbitrary); the hidden scrollbar is a
 * CSS class. Hand-rolled nav-row / chevron / agent / footer `<button>`s keep an
 * `eslint-disable @sparta/no-hand-rolled-button` + citation — no kit primitive
 * matches the bespoke icon+label+chevron row layout.
 */
import * as React from 'react';
import { Icon } from '@sparta/ui-kit';

import { useHiddenNav } from '../../../shared/hooks/useHiddenNav.js';
import type { NavNode, SidebarAgent } from './types.js';
import { BrandMarkV4 } from './BrandMarkV4.js';

export interface SidebarV4Props {
  /** Top-level (or child) id that should read as active. */
  readonly active: string;
  /** Fired when a routable row is chosen. Group rows toggle instead. */
  readonly onSelect: (id: string) => void;
  /** Collapsed (64px icon-only) rail vs the 248px expanded rail. */
  readonly collapsed: boolean;
  /** Toggle the rail's collapsed state (footer chevron + collapsed-group click). */
  readonly onToggleCollapse: () => void;
  /** Fired when an agent quick-list row is chosen. Wired in MENU-5 (SCRUM-301). */
  readonly onAgentChat?: (agentId: string) => void;
  /** Agents shown under Agency. Default `[]` (empty-safe). MENU-5 populates. */
  readonly agents?: readonly SidebarAgent[];
  /** The model to render. REQUIRED — the PMS default was dropped when the
      component left the widget slice (entities must not import `NAV_V4`). */
  readonly navModel: readonly NavNode[];
  /**
   * Predicate: does this destination id resolve to a real route today?
   * Default `() => true` so standalone / Storybook / test usage (and the
   * generic owner shell) stay fully active. The host passes its routable map
   * (PMS: `(id) => hasOwnProperty(NAV_V4_ROUTABLE, id)`).
   *
   * A **routable-destination row** — a top-level leaf, or a group child —
   * whose id is NOT routable renders **disabled** (`aria-disabled`, no
   * `onSelect`, de-emphasized), mirroring the mobile `MobileNavDrawer`'s
   * disabled rows. The sidebar stays tier-1 / portal-agnostic: it knows
   * *whether* an id routes via this predicate, never the route table itself.
   *
   * EXEMPT (always interactive, never disabled): **group rows** (they
   * toggle/expand, they don't route) and **Agency** (the special leaf that
   * expands the agent quick-list — a real affordance; its route lands in
   * MENU-6). Only *purely* dead rows (a route-only leaf/child with no route
   * and no expand) get the disabled treatment. This is the disabled CONTRACT
   * only — real routes are wired in MENU-6 (SCRUM-302).
   */
  readonly isRoutable?: (id: string) => boolean;
  /** Brand wordmark. Default "Desata" (plan Q5). */
  readonly brand?: string;
  /**
   * Edit-menu persistence key — the `localStorage` key the shared `useHiddenNav`
   * store reads/writes for the hidden top-level set (MENU-4 / SCRUM-300). The
   * host passes the SAME key to its own `useHiddenNav` so the desktop sidebar
   * and the compact `MobileNavDrawer` share one hidden set.
   */
  readonly storageKey?: string;
}

/** Which top-level group (if any) owns the active child id. */
function ownerGroupId(
  model: readonly NavNode[],
  active: string,
): string | null {
  for (const node of model) {
    if (node.children?.some((c) => c.id === active)) return node.id;
  }
  return null;
}

/**
 * `true` when the user prefers reduced motion. Read once on mount + on change
 * so the chevron-rotation / group-expand transition classes can be dropped
 * (ACCESSIBILITY.md: instant state change, no rotate/slide). SSR-safe.
 */
function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = React.useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (): void => setReduced(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export function SidebarV4({
  active,
  onSelect,
  collapsed,
  onToggleCollapse,
  onAgentChat,
  agents = [],
  navModel,
  isRoutable = () => true,
  brand = 'Desata',
  storageKey = 'desata.v4.hiddenNav',
}: SidebarV4Props): React.ReactElement {
  // Auto-open the group that owns the active child on first render.
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    () => {
      const owner = ownerGroupId(navModel, active);
      return owner !== null ? { [owner]: true } : {};
    },
  );
  const openGroup = (id: string): void =>
    setOpenGroups((prev) => ({ ...prev, [id]: true }));
  const toggleGroup = (id: string): void =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  // Keep the group that owns the active child open as `active` changes. The
  // host (`PmsShellV4`) stays mounted across nested-route changes, so the
  // mount-only initializer above is not enough: navigating e.g. `/` → `/users`
  // (active becomes the `ops-users` child) must open Operations to reveal the
  // active child rather than leave it hidden. MERGE into `openGroups` — never
  // clobber other groups the user opened/closed by hand (we only ever set the
  // owner group to `true`).
  React.useEffect(() => {
    const owner = ownerGroupId(navModel, active);
    if (owner === null) return;
    setOpenGroups((prev) =>
      prev[owner] === true ? prev : { ...prev, [owner]: true },
    );
  }, [active, navModel]);

  const reducedMotion = usePrefersReducedMotion();
  const chevronMotion = reducedMotion
    ? ''
    : 'transition-transform duration-[var(--dur-fast)]';

  // Edit-menu mode (MENU-4): the hidden top-level set is a shared user
  // preference (same `storageKey` drives the compact MobileNavDrawer via the
  // host). `editing` is an EXPANDED-rail affordance — collapsing suspends it.
  const { isHidden, setHide, isHideable } = useHiddenNav(storageKey);
  const [editing, setEditing] = React.useState(false);
  const editingActive = editing && !collapsed;
  // Outside edit mode, hidden items drop out (desktop + collapsed alike); in
  // edit mode ALL items render (hidden ones de-emphasized) so they're
  // restorable. Children/groups are not independently hideable.
  const visibleNav = editingActive
    ? navModel
    : navModel.filter((node) => !isHidden(node.id));

  return (
    <aside
      className={`${
        collapsed
          ? 'w-[var(--sidebar-w-collapsed)]'
          : 'w-[var(--sidebar-w)]'
      } shrink-0 h-full flex flex-col bg-[var(--bg-surface)] border-r border-solid border-[var(--border-subtle)] ${
        reducedMotion ? '' : 'transition-[width] duration-[var(--dur-base)]'
      }`}
    >
      {/* Brand header — `h-14` (56px) matches the top bar so the shell's two
          header strips align. Collapsed → mark only (no wordmark). */}
      <div
        className={`flex items-center h-14 shrink-0 border-b border-solid border-[var(--border-subtle)] ${
          collapsed ? 'justify-center px-2' : 'gap-2.5 px-4'
        }`}
      >
        <BrandMarkV4 size={28} brand={brand} showWordmark={!collapsed} />
      </div>

      {/* Scrollable nav list — the rail owns its OWN scroll so the shell's
          <main> stays the single page scroll region. The scrollbar is hidden
          via the `.sparta-v4-nav-scroll` CSS rule (index.css), NOT an inline
          numeric style (SCRUM-78); the region stays keyboard-scrollable and
          the focus ring survives (ACCESSIBILITY.md). */}
      <nav
        aria-label="Primary navigation"
        className={`sparta-v4-nav-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2.5 flex flex-col gap-0.5 ${
          collapsed ? 'items-center' : ''
        }`}
      >
        {visibleNav.map((node) => {
          const hasChildren = node.children !== undefined;
          const isAgency = node.id === 'agency';
          const isExpandable = hasChildren || isAgency;
          const isOpen = openGroups[node.id] === true;
          const childActive =
            node.children?.some((c) => c.id === active) ?? false;
          // Closed parent of the active child reads as the active row; an open
          // group does not (its active child carries the accent instead).
          const isActive = node.id === active || (childActive && !isOpen);
          // A top-level LEAF (not a group, not Agency) is a routable
          // destination. When the host's `isRoutable` predicate says its id has
          // no route yet, render it DISABLED (non-interactive, de-emphasized) —
          // the same routability/disabled contract MobileNavDrawer applies on
          // compact — instead of a dead button that silently no-ops on click
          // (Simon P2, SCRUM-298). Group rows + Agency are EXEMPT (they
          // toggle/expand, they don't route), so gate on `!isExpandable`.
          const isDisabled = !isExpandable && !isRoutable(node.id);
          // A disabled row can never be the active route; gate the active
          // affordances defensively so a stale `active` can't paint it current.
          const showActive = isActive && !isDisabled;
          // Edit mode (MENU-4): is this top-level item currently hidden? Hidden
          // items only render in edit mode (so they're restorable), dimmed.
          const nodeHidden = isHidden(node.id);

          /** Row click: in edit mode a click toggles hide/restore (never routes
              or expands). Otherwise the honest collapsed rule is load-bearing
              (Codex blocked twice): collapsed containers + Agency EXPAND the
              rail; only collapsed routable leaves route. */
          const onRowClick = (): void => {
            if (editingActive) {
              // Permanent items (Dashboard) show no control + never toggle.
              if (isHideable(node.id)) setHide(node.id, !nodeHidden);
              return;
            }
            if (collapsed) {
              if (isExpandable) {
                // Group OR Agency: expand the rail + queue this group open.
                // NEVER fire onSelect — the id has no route at this rail state.
                onToggleCollapse();
                openGroup(node.id);
              } else {
                // Routable leaf: route normally.
                onSelect(node.id);
              }
              return;
            }
            // Expanded rail.
            if (isAgency) {
              // The one leaf that BOTH routes AND expands its quick-list.
              onSelect(node.id);
              toggleGroup(node.id);
            } else if (hasChildren) {
              toggleGroup(node.id);
            } else {
              onSelect(node.id);
            }
          };

          return (
            <div key={node.id} className={collapsed ? 'w-full' : undefined}>
              {/* Row wrapper — the row button and the independent chevron are
                  SIBLINGS (not nested): a button-inside-a-button is the
                  `nested-interactive` axe violation. The wrapper is `relative`
                  so the active accent bar + the chevron can position against
                  the full row, while each control stays its own focusable
                  element (ACCESSIBILITY.md: "separate focusable control"). */}
              <div className="relative flex items-center">
                {/* Left accent bar for the active row. `w-0.5` (2px) is the
                    on-scale thin bar — a pixel-literal arbitrary (`w-[3px]`)
                    fails SCRUM-79 in entities/. */}
                {showActive ? (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-[var(--brand-500)] z-10"
                  />
                ) : null}
                {/* Edit-mode add/remove control — a SEPARATE focusable button
                    (sibling of the row, `stopPropagation`). Green plus = Add to
                    menu (when hidden), red minus = Remove from menu (when
                    visible): icon SHAPE + the control carry the state, not
                    colour alone (a11y). Hidden when the rail is collapsed. */}
                {editingActive && isHideable(node.id) ? (
                  // eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke edit add/remove control: a 44px transparent hit target (WCAG 2.1 AA ≥44×44px touch-target floor, CLAUDE.md:360; MENU-7 / SCRUM-303) wraps a 24px tinted chip, so it keeps its visual density; no kit primitive matches (SCRUM-300).
                  <button
                    type="button"
                    aria-label={
                      // Include the row label so screen-reader users know which
                      // item the focused control affects (Simon review P3,
                      // SCRUM-300) — e.g. "Remove Operations from menu".
                      nodeHidden
                        ? `Add ${node.label} to menu`
                        : `Remove ${node.label} from menu`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      setHide(node.id, !nodeHidden);
                    }}
                    className="shrink-0 inline-flex items-center justify-center w-11 h-11 min-w-touch min-h-touch mr-0.5 border-none bg-transparent cursor-pointer"
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-flex items-center justify-center w-6 h-6 rounded-[var(--radius-sm)] ${
                        nodeHidden
                          ? 'bg-[color-mix(in_oklab,var(--success-500)_16%,transparent)] text-[var(--success-500)]'
                          : 'bg-[color-mix(in_oklab,var(--danger-500)_16%,transparent)] text-[var(--danger-500)]'
                      }`}
                    >
                      <Icon
                        name={nodeHidden ? 'plus' : 'minus'}
                        size={14}
                        aria-hidden
                      />
                    </span>
                  </button>
                ) : null}
                {/* Row: leaf rows route; group/Agency rows toggle (expanded) or
                    expand-the-rail (collapsed). See `onRowClick`. A routable
                    leaf with no route today (`isDisabled`) renders disabled. */}
                {/* eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke nav row (icon + optional label); no kit primitive matches this layout (SCRUM-298). */}
                <button
                  type="button"
                  aria-label={node.label}
                  aria-current={showActive ? 'page' : undefined}
                  aria-expanded={
                    !editingActive && isExpandable ? isOpen : undefined
                  }
                  // In edit mode every row is interactive (toggles hide/restore)
                  // even a non-routable one — so only disable outside edit mode.
                  disabled={isDisabled && !editingActive}
                  title={node.label}
                  onClick={isDisabled && !editingActive ? undefined : onRowClick}
                  className={`relative w-full h-11 min-h-touch flex items-center gap-3 rounded-[var(--radius-md)] border-none text-sm ${
                    isDisabled && !editingActive
                      ? 'cursor-not-allowed'
                      : 'cursor-pointer'
                  } ${editingActive && nodeHidden ? 'opacity-50' : ''} ${
                    reducedMotion
                      ? ''
                      : 'transition-[background] duration-[var(--dur-fast)]'
                  } ${collapsed ? 'justify-center px-0' : 'px-3'} ${
                    // Pad the right so the label never slides under the chevron
                    // (chevron is hidden in edit mode, so no pad then).
                    !collapsed && isExpandable && !editingActive ? 'pr-9' : ''
                  } ${
                    isDisabled && !editingActive
                      ? 'bg-transparent text-[var(--fg-disabled)] font-medium'
                      : showActive
                        ? 'bg-[var(--bg-active)] text-[var(--brand-600)] font-semibold'
                        : 'bg-transparent text-[var(--fg-secondary)] font-medium hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {node.icon !== undefined ? (
                    <Icon name={node.icon} size={18} aria-hidden />
                  ) : null}
                  {!collapsed ? (
                    <span className="flex-1 text-left whitespace-nowrap overflow-hidden text-ellipsis">
                      {node.label}
                    </span>
                  ) : null}
                </button>
                {/* Independent chevron — a SEPARATE focusable control (sibling
                    of the row button) that stopPropagation()s so it toggles
                    WITHOUT routing the parent (landmine #3). Positioned over the
                    row's right edge. Hidden on the collapsed rail. */}
                {!collapsed && isExpandable && !editingActive ? (
                  // eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke expand caret overlaying the row's right gutter; small (20px) by design. Intentionally below the 44px touch floor: it is a REDUNDANT affordance — the 44px group row toggles the same group — so WCAG 2.5.5's equivalent-control exception applies (SCRUM-298/303). A 44px caret here would hijack row clicks.
                  <button
                    type="button"
                    aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.label}`}
                    aria-expanded={isOpen}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(node.id);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)] border-none bg-transparent cursor-pointer text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] z-20"
                  >
                    <Icon
                      name="chevDown"
                      size={14}
                      aria-hidden
                      className={`${chevronMotion} ${
                        isOpen ? 'rotate-0' : '-rotate-90'
                      }`}
                    />
                  </button>
                ) : null}
              </div>

              {/* Agency → agent quick-chat list (expanded + open only). Renders
                  from the injected `agents` prop; when empty, shows an honest
                  placeholder line — not fabricated rows (MENU-5 / SCRUM-301). */}
              {isAgency && isOpen && !collapsed && !editingActive ? (
                <div className="flex flex-col gap-px mt-0.5 mb-1">
                  {/* Honest empty placeholder (MENU-5 / SCRUM-301): there is no
                      agents backend yet, so the expanded Agency shows this line
                      rather than fabricated rows. Real agents + chat arrive when
                      the backend lands (see the BE-need). Deliberate change to
                      MENU-2's empty-path receipt: empty `agents` → no agent rows
                      / no crash, and now also this line. */}
                  {agents.length === 0 ? (
                    <p className="m-0 pl-8 pr-3 py-2 text-[13px] text-[var(--fg-tertiary)]">
                      No agents connected yet
                    </p>
                  ) : null}
                  {agents.map((agent) => (
                    // eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke agent row (avatar dot + persona + msg icon); no kit primitive matches (SCRUM-298).
                    <button
                      key={agent.id}
                      type="button"
                      aria-label={`Chat with ${agent.persona}`}
                      onClick={() => onAgentChat?.(agent.id)}
                      className={`w-full h-11 min-h-touch flex items-center gap-2.5 pl-8 pr-3 rounded-[var(--radius-md)] border-none bg-transparent cursor-pointer hover:bg-[var(--bg-hover)] ${
                        reducedMotion
                          ? ''
                          : 'transition-[background] duration-[var(--dur-fast)]'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className="w-5 h-5 rounded-full shrink-0 bg-[var(--brand-500)]"
                        style={
                          agent.accent !== undefined
                            ? { background: agent.accent }
                            : undefined
                        }
                      />
                      <span className="flex-1 min-w-0 text-left text-[13px] font-semibold text-[var(--fg-secondary)] whitespace-nowrap overflow-hidden text-ellipsis">
                        {agent.persona}
                      </span>
                      <Icon
                        name="msg"
                        size={13}
                        aria-hidden
                        className="text-[var(--fg-tertiary)] shrink-0"
                      />
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Group children — one level only, expanded + open only; hidden
                  in edit mode (hideable scope is top-level ids only). */}
              {hasChildren && isOpen && !collapsed && !editingActive ? (
                <div className="flex flex-col gap-px mt-px mb-0.5">
                  {node.children?.map((child) => {
                    const childIsActive = child.id === active;
                    // Group children are routable destinations — same disabled
                    // contract as the top-level leaves above (Simon P2).
                    const childDisabled = !isRoutable(child.id);
                    const childShowActive = childIsActive && !childDisabled;
                    return (
                      // eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke nested nav row (dot + label); no kit primitive matches this layout (SCRUM-298).
                      <button
                        key={child.id}
                        type="button"
                        aria-label={child.label}
                        aria-current={childShowActive ? 'page' : undefined}
                        disabled={childDisabled}
                        onClick={
                          childDisabled ? undefined : () => onSelect(child.id)
                        }
                        className={`relative w-full h-8 flex items-center gap-3 pl-10 pr-3 rounded-[var(--radius-md)] border-none text-[13px] ${
                          childDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                        } ${
                          reducedMotion
                            ? ''
                            : 'transition-[background] duration-[var(--dur-fast)]'
                        } ${
                          childDisabled
                            ? 'bg-transparent text-[var(--fg-disabled)] font-medium'
                            : childShowActive
                              ? 'bg-[var(--bg-active)] text-[var(--brand-600)] font-semibold'
                              : 'bg-transparent text-[var(--fg-secondary)] font-medium hover:bg-[var(--bg-hover)]'
                        }`}
                      >
                        {childShowActive ? (
                          <span
                            aria-hidden="true"
                            className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-[var(--brand-500)]"
                          />
                        ) : null}
                        <span
                          aria-hidden="true"
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                            childShowActive
                              ? 'bg-[var(--brand-500)]'
                              : 'bg-[var(--border-strong)]'
                          }`}
                        />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                          {child.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      {/* Footer — collapse chevron + Edit-menu toggle. Collapsed → only the
          expand chevron (Edit button + hint hidden; edit mode is an
          expanded-rail affordance, MENU-4 / SCRUM-300). When editing, a hint
          line appears above the controls + each row shows an add/remove
          control. */}
      <div className="border-t border-solid border-[var(--border-subtle)] p-2.5 shrink-0 flex flex-col gap-2">
        {editingActive ? (
          <p className="m-0 px-1 text-xs text-[var(--fg-tertiary)]">
            Tap an item to add or remove it from your menu.
          </p>
        ) : null}
        <div className="flex gap-2">
          {collapsed ? (
            // eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke footer control (centered icon chip on a sunken surface); composes Icon (SCRUM-298).
            <button
              type="button"
              aria-label="Expand menu"
              title="Expand menu"
              onClick={onToggleCollapse}
              className={`w-full h-11 min-h-touch inline-flex items-center justify-center rounded-[var(--radius-md)] border-none bg-[var(--bg-sunken)] text-[var(--fg-secondary)] cursor-pointer hover:bg-[var(--bg-hover)] ${
                reducedMotion
                  ? ''
                  : 'transition-[background] duration-[var(--dur-fast)]'
              }`}
            >
              <Icon name="chevRight" size={17} aria-hidden />
            </button>
          ) : (
            <>
              {/* eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke footer chip (icon on a sunken surface); no kit primitive matches (SCRUM-298). */}
              <button
                type="button"
                aria-label="Collapse menu"
                title="Collapse menu"
                onClick={onToggleCollapse}
                className={`w-11 h-11 min-w-touch min-h-touch shrink-0 inline-flex items-center justify-center rounded-[var(--radius-md)] border-none bg-[var(--bg-sunken)] text-[var(--fg-secondary)] cursor-pointer hover:bg-[var(--bg-hover)] ${
                  reducedMotion
                    ? ''
                    : 'transition-[background] duration-[var(--dur-fast)]'
                }`}
              >
                <Icon name="chevLeft" size={17} aria-hidden />
              </button>
              {/* Edit-menu toggle — flips edit mode (MENU-4). `aria-pressed`
                  carries the toggle state; label/icon swap Edit ↔ Done. */}
              {/* eslint-disable-next-line @sparta/no-hand-rolled-button -- bespoke footer button (icon + label on a sunken surface); no kit primitive matches (SCRUM-298/300). */}
              <button
                type="button"
                aria-label={editing ? 'Done editing' : 'Edit menu'}
                aria-pressed={editing}
                title={editing ? 'Done editing' : 'Edit menu'}
                onClick={() => setEditing((prev) => !prev)}
                className={`flex-1 h-11 min-h-touch inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)] border-none text-[13px] font-semibold cursor-pointer ${
                  reducedMotion
                    ? ''
                    : 'transition-[background] duration-[var(--dur-fast)]'
                } ${
                  editing
                    ? 'bg-[var(--bg-active)] text-[var(--brand-600)]'
                    : 'bg-[var(--bg-sunken)] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <Icon
                  name={editing ? 'check' : 'settings'}
                  size={15}
                  aria-hidden
                />
                {editing ? 'Done editing' : 'Edit menu'}
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
